import { LOG } from 'src/app/common/util/log';

import {
  Alerter,
  AlerterOperator,
  AlerterOperators,
  BoundActionFn,
  cellStyleClasses,
  TableColumnMetadata,
} from '../types';
import { Alignment, Alignments } from '../types/aligments';

export class TableMetadataHandler {
  public readonly metadata: TableColumnMetadata[] = [];

  public addAlerter(columnIndex: number, greenOperator: string, redOperator: string, neutral: string): void {
    if (!Object.values(AlerterOperators).includes(greenOperator as AlerterOperators)) {
      LOG.error('Invalid alerter operator:', greenOperator);
      return;
    }

    if (!Object.values(AlerterOperators).includes(redOperator as AlerterOperators)) {
      LOG.error('Invalid alerter operator:', redOperator);
      return;
    }

    if (greenOperator === redOperator) {
      LOG.error('Alerter operators must be different.');
      return;
    }

    const neutralValue: number = Number.parseFloat(neutral);

    if (Number.isNaN(neutralValue)) {
      LOG.error('Invalid alerter neutral value:', neutral);
      return;
    }

    const alerter: Alerter = {
      greenOperator: greenOperator as AlerterOperator,
      redOperator: redOperator as AlerterOperator,
      neutralValue: neutralValue,
    };

    this.getMetadata(columnIndex).alerter = alerter;
  }

  public addCellStyle(columnIndex: number, styles: string[]): void {
    if (!styles.every(style => cellStyleClasses.has(style))) {
      LOG.error('Invalid cell style(s):', ...styles.filter(style => !cellStyleClasses.has(style)));
      styles = styles.filter(style => cellStyleClasses.has(style));
    }

    if (styles.length === 0) {
      return;
    }

    const meta = this.getMetadata(columnIndex);
    meta.styleClasses = (meta.styleClasses ?? []).concat(styles.map(style => cellStyleClasses.get(style)));
  }

  public addAction(columnIndex: number, action: BoundActionFn): void {
    if (typeof action !== 'function') {
      throw new Error('Action must be function.');
    }

    this.getMetadata(columnIndex).action = action;
  }

  public actionsExcludeGroupHeader(columnIndex: number): void {
    this.getMetadata(columnIndex).actionExcludeGroupHeader = true;
  }

  public addAlign(columnIndex: number, alignment: string): void {
    if (!Object.values(Alignments).includes(alignment as Alignments)) {
      LOG.error('Invalid cell alignment:', alignment);
      return;
    }
    this.getMetadata(columnIndex).align = alignment as Alignment;
  }

  public addUnsortable(columnIndex: number): void {
    this.getMetadata(columnIndex).unsortable = true;
  }

  public addSortBy(columnIndex: number, option: 'label' | 'value'): void {
    this.getMetadata(columnIndex).sortBy = option;
  }

  private getMetadata(columnIndex: number): TableColumnMetadata {
    if (columnIndex < 0) {
      throw new Error(`Invalid column index: ${columnIndex}`);
    }

    while (this.metadata.length <= columnIndex) {
      this.metadata.push(null);
    }

    return this.metadata[columnIndex] ?? (this.metadata[columnIndex] = {});
  }
}
